/** Single source of truth — matches BE `cpscPgaDatasetMode` and message-set radio values */
export const CPSC_DATASET_MODE = Object.freeze({
  PRE_REGISTERED: 'PRE_REGISTERED',
  FULL_DATASET: 'FULL_DATASET',
})

/** BE PGAId for dataset mode */
export const CPSC_DATASET_MODE_PGA_ID = 'cpscPgaDatasetMode'

const VALID_DATASET_MODES = Object.freeze(Object.values(CPSC_DATASET_MODE))

/** Returns `PRE_REGISTERED` / `FULL_DATASET` when rawValue matches BE exactly, else ''. */
export function normalizeCpscDatasetModeValue(rawValue) {
  const trimmed = (rawValue != null ? String(rawValue) : '').trim()
  return VALID_DATASET_MODES.includes(trimmed) ? trimmed : ''
}

/** HTS tariff CP2: CPSC still shows as needed, but UI completion validations are skipped */
export const CPSC_CP2_TARIFF_CODE = 'CP2'
export const isCpscCp2Tariff = (pgaDetail) => pgaDetail?.tariffCode === CPSC_CP2_TARIFF_CODE
export const hasCpscCp2Tariff = (tarriffPgaDetails = []) =>
  (tarriffPgaDetails || []).some(isCpscCp2Tariff)
export const shouldSkipCpscUiValidation = (hsDataDtl) =>
  hasCpscCp2Tariff(hsDataDtl?.pgaMetadata?.tarriffPgaDetails)
export const shouldSkipIncompleteCpscFlag = (oneFlag, tarriffPgaDetails) =>
  oneFlag?.formType === 'CPSC' && hasCpscCp2Tariff(tarriffPgaDetails)

export const MESSAGE_SET_OPTIONS = [
  {
    value: CPSC_DATASET_MODE.PRE_REGISTERED,
    title: 'Pre-Registered CPSC Product (Minimal Data Set)',
    description:
      'Use this if your product is already registered in the CPSC Product Registry. You only need to provide 3 reference identifiers.',
    tags: ['Faster processing', 'Less data entry', 'Requires Product Registry account'],
    tone: 'positive',
  },
  {
    value: CPSC_DATASET_MODE.FULL_DATASET,
    title: 'Unregistered Product (File Full Data Set)',
    description:
      'Use this if your product is NOT in the CPSC Product Registry. You must provide complete certificate information with your entry.',
    tags: ['More data required', 'Complete certificate details needed'],
    tone: 'warning',
  },
]

// PG01 certificate type options (CPSC)
export const CERTIFICATE_TYPE_OPTIONS = [
  { value: 'FCP', description: "FCP/CPC - Children's Product Certificate", hidePrefixOnOption: true },
  { value: 'FGC', description: 'FGC/GCC - General Conformity Certificate', hidePrefixOnOption: true },
]

// PG01 intended use codes (CPSC disclaim) — same shape as CERTIFICATE_TYPE_OPTIONS; `value` is the code sent to BE.
export const CPSC_INTENDED_USE_CODE_OPTIONS = [
  {
    value: '130.000',
    description:
      '(Generic) For Consumer Use as a Non-Food Product (besides 130.001-005)',
  },
  {
    value: '130.006',
    description: '(Specific) Consumer product intended for People aged 13 Years or Older',
  },
  { value: '081.000', description: 'For Human Medical Use as Medical Device' },
  { value: '090.000', description: 'For Military Use as a Non-Food Product' },
  { value: '100.000', description: 'For Personal Use as a Non-Food Product' },
  { value: '155.000', description: 'For Commercial Assembly as a Non-Food Product' },
  {
    value: '940.000',
    description: 'For Compassionate/Emergency Use of a Non-Food Product',
  },
  { value: '970.000', description: 'For Export' },
]

// PG10 product identifier qualifier options — value must match BE productCodeQualifier
export const PRODUCT_ID_TYPE_OPTIONS = [
  { value: 'SRV', description: 'GS1 Global Trade Item Number (GTIN)' },
  { value: 'AI', description: 'UPC (Universal Product Code)' },
  { value: 'SKU', description: 'SKU', hidePrefixOnOption: true },
  { value: 'MN', description: 'Model Number' },
  { value: 'SE', description: 'Serial Number' },
  { value: 'RN', description: 'Registered Number' },
  { value: 'ALT', description: 'Alternate Identifier' },
]

// Groups of PG10 qualifiers where choosing one excludes all others across rows.
// GTIN (SRV) and UPC (AI) are different encodings of the same barcode — only one per line item.
const PRODUCT_ID_EXCLUSIVE_GROUPS = [['SRV', 'AI']]

// Returns PG10 qualifier options available to a row — removes duplicates and mutually-exclusive group peers (e.g. GTIN vs UPC)
export function getAvailableProductIdTypeOptions(assignedQualifiers = [], selfQualifier = '') {
  const assignedByOthers = assignedQualifiers.filter(
    (value) => value && value !== selfQualifier
  )
  const groupsLockedByOthers = PRODUCT_ID_EXCLUSIVE_GROUPS.filter((group) =>
    group.some((value) => assignedByOthers.includes(value))
  )
  return PRODUCT_ID_TYPE_OPTIONS.filter((opt) => {
    if (assignedByOthers.includes(opt.value)) return false
    return !groupsLockedByOthers.some((group) => group.includes(opt.value))
  })
}

// CPSC repeated list keys shared across parser/reducer/UI
export const CPSC_REPEATED_LISTS = {
  PRODUCT_IDENTIFIER: 'CPSC_PRODUCT_IDENTIFIER_List',
  ENTITY_ITL: 'CPSC_ENTITY_ITL_List',
  ENTITY_LAB: 'CPSC_ENTITY_LAB_List',
  CITATION_ITL: 'CPSC_CITATION_ITL_List',
  CITATION_LAB: 'CPSC_CITATION_LAB_List',
  CITATION_NOL: 'CPSC_CITATION_NOL_List',
  TEST_REPORT_ITL: 'CPSC_TEST_REPORT_ITL_List',
  TEST_REPORT_LAB: 'CPSC_TEST_REPORT_LAB_List',
}

export const CPSC_ENTITY_FIELDS = {
  ITL: ['entityNumberITL'],
  LAB: [
    'individualNameLAB',
    'entityNameLAB',
    'countryLAB',
    'stateLAB',
    'entityAddress1LAB',
    'entityAddress2LAB',
    'cityLAB',
    'zipLAB',
    'emailLAB',
    'telNumberLAB',
  ],
}

// CPSC citation PGAIds by entity
export const CPSC_CITATION_FIELDS = {
  ITL: 'citationITL',
  LAB: 'citationLAB',
  NOL: 'citationNOL',
}

// PG60 test report — repeatable qualifier (CP1–CP4) + value; BE PGAIds per entity
export const CPSC_TEST_REPORT_FIELDS = {
  QUALIFIER_ITL: 'additionalInfoQualifierCodeITL',
  INFORMATION_ITL: 'testReportUrlITL',
  QUALIFIER_LAB: 'additionalInfoQualifierCodeLAB',
  INFORMATION_LAB: 'testReportUrlLAB',
}

/** PG60 report-type codes (CP1–CP4) — values sent as additionalInfoQualifierCodeITL/LAB */
export const TEST_REPORT_TYPE_OPTIONS = [
  { value: 'CP1', description: 'CP1: Lab test report ID' },
  { value: 'CP2', description: 'CP2: Lab test report URL' },
  { value: 'CP3', description: 'CP3: Access key to lab test report URL' },
  { value: 'CP4', description: 'CP4: Component part description' },
]

// PGAIds for repeatable CPSC product identifier rows (PG10); keep in sync with getCPSC_PRODUCT_IDENTIFIER_Values / assignValues
export const CPSC_PRODUCT_IDENTIFIER_FIELDS = {
  ITEM_TYPE: 'productCodeQualifier',
  PRODUCT_CODE_NO: 'productCodeNo',
}

// Entity type cards — code matches BE field suffix (e.g. entityNameCE, entityAddress1CE)
export const ENTITY_TYPE_CARDS = [
  {
    code: 'MF',
    title: 'MF - Manufacturer of Goods',
    description: 'Entity that manufactures the product',
  },
  {
    code: 'ITL',
    title: 'ITL - Independent Third Party Laboratory',
    description: 'CPSC Accepted Testing Lab',
  },
  {
    code: 'LAB',
    title: 'LAB - Laboratory',
    description: 'Non-CPSC-Accepted Lab',
  },
  {
    code: 'NOL',
    title: 'NOL - No Lab Testing Required',
    description: 'Testing exclusions',
  },
  {
    code: 'CE',
    title: 'CE - Certifying Entity information',
    description: 'Importer, Manufacturer, or Private Labeler',
  },
  {
    code: 'PK',
    title: 'PK - Point of Contact',
    description: 'Individual maintaining test records',
  },
]

/** True when BE sent this PGA field on the form (flat vals or repeated row). */
export function hasCpscPgaField(values = {}, fieldKey) {
  const field = values?.[fieldKey]
  return Boolean(field && typeof field === 'object' && field.PGAId)
}

/** ActorWithContact flags derived from BE field presence — no hardcoded per-party map. */
function getCpscPartyActorFlags(values = {}, code) {
  return {
    showEntityNumber: hasCpscPgaField(values, `entityNumber${code}`),
    showIdentificationCode: hasCpscPgaField(values, `entityIdentificationCode${code}`),
  }
}

function getCpscPartyHasContact(values = {}, code) {
  return ['individualName', 'email', 'telNumber'].some((prefix) =>
    hasCpscPgaField(values, `${prefix}${code}`)
  )
}

/** MF entity code — add SBM; N/A uses the shared NONE option from ActorWithContact. */
export const CPSC_MF_ENTITY_CODE_OPTIONS = [
  { value: 'SBM', description: 'Small Batch Manufacturer' },
]

/** Entity number is editable for MF only when Small Batch Manufacturer is selected. */
export function isCpscMfEntityNumberDisabled(values = {}) {
  return values?.entityIdentificationCodeMF?.value !== 'SBM'
}

/** ActorWithContact props derived from BE field presence. */
export function getCpscActorProps(values = {}, code) {
  const { showEntityNumber, showIdentificationCode } = getCpscPartyActorFlags(values, code)
  return {
    hasContact: getCpscPartyHasContact(values, code),
    doesntHaveEntity: !showEntityNumber,
    doesntHaveEntityIdentificationCodeField: !showIdentificationCode,
    ...(code === 'MF' && showIdentificationCode
      ? {
          entityIdTypes: CPSC_MF_ENTITY_CODE_OPTIONS,
          entityNumberDisabled: isCpscMfEntityNumberDisabled(values),
        }
      : {}),
  }
}

// Field suffixes used to detect saved entity-party data (checkbox seeding + validation)
export const CPSC_ENTITY_DATA_SUFFIXES = [
  'entityName',
  'entityAddress1',
  'city',
  'country',
  'entityNumber',
  'individualName',
  'zip',
  'email',
  'telNumber',
]

export function getValidationSelectedEntities(selectedEntities = {}) {
  if (Object.keys(selectedEntities).length > 0) return selectedEntities
  const noneSelected = {}
  ENTITY_TYPE_CARDS.forEach(({ code }) => {
    noneSelected[code] = false
  })
  return noneSelected
}

export const PG30_LAB_TEST_STATUS = Object.freeze({ checked: 'N', unchecked: 'L' })

export const REGISTRY_NOTE =
  'Note: The Certificate Identifiers you provide must exactly match what is stored in your CPSC Product Registry Business Account.'

// CATAIR "X" (Special Data): letters, digits, space and standard keyboard punctuation
export const SPECIAL_DATA_REGEX = /^[A-Za-z0-9 !@#$%^&*()\-_=+\[\]{}\\|;:'",<.>\/?`~]*$/
export const SPECIAL_DATA_REGEX_DESC =
  'valid: use only letters, numbers, spaces and standard keyboard characters'

// Direct address/contact field prefixes for CE/MF/PK (suffix = entity code)
export const CPSC_ENTITY_ADDRESS_FIELDS = [
  'entityName',
  'entityAddress1',
  'entityAddress2',
  'city',
  'state',
  'country',
  'zip',
  'email',
  'telNumber',
  'entityNumber',
  'individualName',
  'entityIdentificationCode',
]

export const CPSC_ENTITY_SECTION_LISTS = {
  ITL: [
    CPSC_REPEATED_LISTS.ENTITY_ITL,
    CPSC_REPEATED_LISTS.CITATION_ITL,
    CPSC_REPEATED_LISTS.TEST_REPORT_ITL,
  ],
  LAB: [
    CPSC_REPEATED_LISTS.ENTITY_LAB,
    CPSC_REPEATED_LISTS.CITATION_LAB,
    CPSC_REPEATED_LISTS.TEST_REPORT_LAB,
  ],
}

const hasPgaValue = (field) =>
  Boolean(field?.value != null && String(field.value).trim() !== '')

const treeHasPgaValues = (node) => {
  if (!node) return false
  if (Array.isArray(node)) return node.some(treeHasPgaValues)
  if (node.PGAId) return hasPgaValue(node)
  if (typeof node === 'object') return Object.values(node).some(treeHasPgaValues)
  return false
}

/** Infer checked entity parties from saved values (UI checkboxes + validation fallback). */
export function inferSelectedEntities(formValues = {}) {
  const selected = {}

  ENTITY_TYPE_CARDS.forEach(({ code }) => {
    const lists =
      CPSC_ENTITY_SECTION_LISTS[code] ||
      (code === 'NOL' ? [CPSC_REPEATED_LISTS.CITATION_NOL] : [])
    const hasFlatData = Object.entries(formValues).some(([key, field]) => {
      if (!key.endsWith(code) || key.startsWith('entryRoleCode')) return false
      return field?.PGAId && hasPgaValue(field)
    })
    const hasListData = lists.some((listName) => treeHasPgaValues(formValues[listName]))
    selected[code] = hasFlatData || hasListData
  })

  return selected
}

// Clears saved PGA data for an entity type when the user unchecks it
export function clearEntityPartyData({
  code,
  vals,
  itlEntityList,
  labEntityList,
  nolCitationList,
  pgaGuid,
  formType,
  merchandiseId,
  lineItemIndex,
  updatePGAField,
  savePGAField,
  removeRepeatedGroup,
  removeRepeatedSection,
}) {
  const flatFields = []

  if (code === 'ITL') {
    flatFields.push('entityNumberITL')
    const count = (itlEntityList || []).length
    for (let i = 0; i < count; i++) {
      removeRepeatedSection(
        pgaGuid,
        formType,
        merchandiseId,
        lineItemIndex,
        count - 1 - i,
        CPSC_ENTITY_SECTION_LISTS.ITL
      )
    }
  } else if (code === 'LAB') {
    flatFields.push(...CPSC_ENTITY_FIELDS.LAB)
    const count = (labEntityList || []).length
    for (let i = 0; i < count; i++) {
      removeRepeatedSection(
        pgaGuid,
        formType,
        merchandiseId,
        lineItemIndex,
        count - 1 - i,
        CPSC_ENTITY_SECTION_LISTS.LAB
      )
    }
  } else if (code === 'NOL') {
    if ((nolCitationList || []).length && removeRepeatedGroup) {
      removeRepeatedGroup(
        pgaGuid,
        formType,
        merchandiseId,
        lineItemIndex,
        -1,
        CPSC_REPEATED_LISTS.CITATION_NOL
      )
    }
  } else {
    CPSC_ENTITY_ADDRESS_FIELDS.forEach((field) => flatFields.push(`${field}${code}`))
  }

  const fieldsToSave = []
  flatFields.forEach((fieldName) => {
    const field = vals?.[fieldName]
    if (!field || !field.value) return
    updatePGAField(pgaGuid, formType, merchandiseId, fieldName, '')
    fieldsToSave.push(fieldName)
  })
  if (fieldsToSave.length) {
    savePGAField(pgaGuid, formType, merchandiseId, lineItemIndex, fieldsToSave)
  }
}

